import { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

import Card from "@mui/material/Card";
import Checkbox from "@mui/material/Checkbox";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Tooltip from "@mui/material/Tooltip";

import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import SoftPagination from "components/SoftPagination";
import SoftSelect from "components/SoftSelect";
import SoftSnackbar from "components/SoftSnackbar";
import SoftTypography from "components/SoftTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import { API_URL } from "config";
import { useAuth } from "../../AuthContext";
import { useModulePermissions } from "utils/modulePermissions";
import {
  INVOICE_TABS,
  invoiceActionPermission,
  invoiceListColumnPermission,
  invoiceListControlPermission,
  invoicePermission,
  invoiceWorkflowPermission,
} from "./invoicePermissions";

const tabColors = {
  total: { color: "#0ea5e9", background: "#e0f2fe", border: "#7dd3fc" },
  current_month: { color: "#2563eb", background: "#dbeafe", border: "#93c5fd" },
  paid: { color: "#16a34a", background: "#dcfce7", border: "#86efac" },
  overdue: { color: "#f59e0b", background: "#fff7df", border: "#ffd666" },
  cancelled: { color: "#ef4444", background: "#fee2e2", border: "#fecaca" },
};

const headerCellSx = {
  backgroundColor: "#f8f9fa",
  color: "#64748b",
  fontSize: "10px",
  fontWeight: 600,
  py: 0.9,
  px: 0.3,
  whiteSpace: "normal",
  lineHeight: 1.15,
  borderBottom: "1px solid #e9ecef",
};

const bodyCellSx = {
  color: "#334155",
  fontSize: "10px",
  py: 0.7,
  px: 0.3,
  verticalAlign: "top",
  borderBottom: "1px solid #edf2f7",
  overflowWrap: "anywhere",
};

const emptyPagination = { current_page: 1, last_page: 1, per_page: 50, total: 0, from: 0, to: 0 };

const workflowActionOptions = [
  { value: "status", label: "Change Status" },
  { value: "payment_status", label: "Change Payment Status" },
  { value: "email_status", label: "Change Email Status" },
];
const workflowValueOptions = {
  status: [
    { value: "approved", label: "Approved" },
    { value: "cancelled", label: "Cancelled" },
    { value: "rejected", label: "Rejected" },
  ],
  payment_status: [
    { value: "pending", label: "Payment Pending" },
    { value: "paid", label: "Paid" },
  ],
  email_status: [
    { value: "pending", label: "Pending" },
    { value: "sent", label: "Sent" },
  ],
};
const workflowFieldLabels = {
  status: "Status",
  payment_status: "Payment Status",
  email_status: "Email Status",
};
const selectedOption = (options, value) => options.find((option) => option.value === value) || null;
const responseErrorMessage = (result, fallback) => (
  Object.values(result?.errors || {}).flat().find(Boolean) || result?.message || fallback
);

const formatNumber = (value) => Number(value || 0).toLocaleString(undefined, {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const currencySymbols = { USD: "$", INR: "₹", GBP: "£", EUR: "€", EURO: "€", AED: "د.إ", SGD: "S$" };
const currencySymbolFor = (row) => {
  const code = String(row.currency_code || "").toUpperCase();
  return currencySymbols[code] || row.currency_symbol || code;
};

const formatCurrency = (value, symbol, code, includeCode = false) => {
  const normalizedCode = String(code || "").toUpperCase();
  const currencySymbol = symbol || normalizedCode;
  const symbolSeparator = /^[A-Z]{2,5}$/.test(String(currencySymbol)) ? " " : "";
  return `${currencySymbol}${symbolSeparator}${formatNumber(value)}${includeCode && normalizedCode ? ` ${normalizedCode}` : ""}`;
};

const projectCodesForRow = (row) => {
  if (Array.isArray(row.project_codes) && row.project_codes.length > 0) {
    return row.project_codes.map((code) => String(code).trim()).filter(Boolean);
  }
  return String(row.project_code || "")
    .split(",")
    .map((code) => code.trim())
    .filter(Boolean);
};

const remainingDueDays = (dueDate) => {
  const match = String(dueDate || "").match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;
  const due = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.round((due.getTime() - today.getTime()) / 86400000);
};

function DueCountdown({ row }) {
  if (row.payment_status === "paid" || ["cancelled", "rejected"].includes(row.status)) return null;
  const days = remainingDueDays(row.due_date);
  if (days === null) return null;

  if (days > 0) {
    return (
      <SoftBox mt={0.25}>
        <SoftTypography variant="caption" display="block" sx={{ lineHeight: 1.2 }}>due in</SoftTypography>
        <SoftTypography variant="caption" display="block" sx={{ color: "#16a34a", fontWeight: 700, lineHeight: 1.2 }}>
          {days} {days === 1 ? "day" : "days"}
        </SoftTypography>
      </SoftBox>
    );
  }
  if (days === 0) {
    return <SoftTypography variant="caption" display="block" color="warning" fontWeight="bold" mt={0.25}>due today</SoftTypography>;
  }
  return (
    <SoftTypography variant="caption" display="block" color="error" fontWeight="bold" mt={0.25}>
      overdue by<br />{Math.abs(days)} {Math.abs(days) === 1 ? "day" : "days"}
    </SoftTypography>
  );
}

DueCountdown.propTypes = {
  row: PropTypes.shape({
    due_date: PropTypes.string,
    payment_status: PropTypes.string,
    status: PropTypes.string,
  }).isRequired,
};

function ClearableDate({ label, value, onChange }) {
  return (
    <SoftBox sx={{ position: "relative", width: "100%", minWidth: 0 }}>
      <SoftTypography variant="caption" display="block" mb={0.35}>{label}</SoftTypography>
      <SoftInput size="small" type="date" value={value} onChange={(event) => onChange(event.target.value)} />
      {value && (
        <IconButton
          size="small"
          aria-label={`Clear ${label}`}
          onClick={() => onChange("")}
          sx={{ position: "absolute", right: 28, bottom: 3, p: 0.45, backgroundColor: "#fff" }}
        >
          <Icon sx={{ fontSize: "15px !important" }}>close</Icon>
        </IconButton>
      )}
    </SoftBox>
  );
}

ClearableDate.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
};

function InvoiceList({ invoiceType }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { permissions = [], userPermission = [] } = useAuth();
  const moduleName = `invoices.${invoiceType}`;
  const { hasModulePermission } = useModulePermissions(permissions, userPermission, moduleName);
  const visibleTabs = useMemo(
    () => INVOICE_TABS.filter((item) => hasModulePermission(invoicePermission(invoiceType, "tabs", item.value))),
    [hasModulePermission, invoiceType]
  );
  const requestedTab = searchParams.get("tab");
  const [tab, setTab] = useState(requestedTab || "total");
  const [filters, setFilters] = useState({
    startDate: "", endDate: "", status: null, paymentStatus: null, invoiceMonth: null,
    emailStatus: null, party: null, country: null, search: "",
  });
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({});
  const [grandTotal, setGrandTotal] = useState(0);
  const [options, setOptions] = useState({ parties: [], invoice_months: [], countries: [] });
  const [serverControls, setServerControls] = useState({});
  const [pagination, setPagination] = useState(emptyPagination);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [snackbar, setSnackbar] = useState({ open: false, message: "", color: "success" });
  const [selectedInvoiceIds, setSelectedInvoiceIds] = useState([]);
  const [bulkAction, setBulkAction] = useState("");
  const [bulkValue, setBulkValue] = useState("");
  const [workflowSaving, setWorkflowSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [pdfExporting, setPdfExporting] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuInvoice, setMenuInvoice] = useState(null);
  const [workflowDialog, setWorkflowDialog] = useState({
    open: false,
    field: "status",
    value: "approved",
    invoice: null,
  });
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    const successMessage = location.state?.invoiceSuccessMessage;
    if (!successMessage) return;
    setSnackbar({ open: true, message: successMessage, color: "success" });
    navigate(`${location.pathname}${location.search}`, { replace: true, state: null });
  }, [location.pathname, location.search, location.state, navigate]);

  const partyLabel = invoiceType === "client" ? "Client" : "Vendor";
  const title = `${partyLabel} Invoices`;
  const canCreateClientInvoice = invoiceType === "client"
    && hasModulePermission("invoices.client.pages.create.view")
    && hasModulePermission(invoiceActionPermission("client", "create"));
  const canSendEmail = hasModulePermission(invoiceActionPermission(invoiceType, "send_email"));
  const canExport = invoiceType === "client" && hasModulePermission(invoiceActionPermission(invoiceType, "export"));
  const canExportPdf = invoiceType === "client" && hasModulePermission(invoiceActionPermission(invoiceType, "export_pdf"));
  const canListColumn = useCallback(
    (key) => hasModulePermission(invoiceListColumnPermission(invoiceType, key)),
    [hasModulePermission, invoiceType]
  );
  const canListControl = useCallback(
    (key) => hasModulePermission(invoiceListControlPermission(invoiceType, key))
      && serverControls[key] !== false,
    [hasModulePermission, invoiceType, serverControls]
  );
  const canUpdateWorkflowField = useCallback(
    (field, bulk = false) => hasModulePermission(invoiceWorkflowPermission(
      invoiceType,
      `${bulk ? "bulk_update" : "update"}_${field}`
    )),
    [hasModulePermission, invoiceType]
  );
  const workflowOptionsForField = useCallback(
    (field) => (workflowValueOptions[field] || []).filter(
      (option) => field !== "email_status" || option.value !== "sent" || canSendEmail
    ),
    [canSendEmail]
  );
  const availableWorkflowActionOptions = workflowActionOptions.filter(
    (option) => canUpdateWorkflowField(option.value)
  );
  const availableBulkWorkflowActionOptions = workflowActionOptions.filter(
    (option) => canUpdateWorkflowField(option.value, true)
  );
  const canUpdateWorkflow = availableWorkflowActionOptions.length > 0;
  const canBulkUpdateWorkflow = availableBulkWorkflowActionOptions.length > 0;
  const canSelectRows = canBulkUpdateWorkflow && canListControl("bulk_selection");
  const canFilter = useCallback(
    (key) => hasModulePermission(invoicePermission(invoiceType, "filters", key)),
    [hasModulePermission, invoiceType]
  );

  useEffect(() => {
    if (visibleTabs.length && !visibleTabs.some((item) => item.value === tab)) {
      setTab(visibleTabs[0].value);
      setPage(1);
    }
  }, [tab, visibleTabs]);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedSearch(filters.search.trim()), 350);
    return () => window.clearTimeout(timer);
  }, [filters.search]);

  const queryString = useMemo(() => {
    const params = new URLSearchParams({ tab, page: String(page), per_page: "50" });
    if (filters.startDate) params.set("start_date", filters.startDate);
    if (filters.endDate) params.set("end_date", filters.endDate);
    if (filters.status?.value) params.set("status", filters.status.value);
    if (filters.paymentStatus?.value) params.set("payment_status", filters.paymentStatus.value);
    if (filters.invoiceMonth?.value) params.set("invoice_month", filters.invoiceMonth.value);
    if (filters.emailStatus?.value) params.set("email_status", filters.emailStatus.value);
    if (filters.party?.value) params.set("party_id", String(filters.party.value));
    if (filters.country?.value) params.set("country", filters.country.value);
    if (debouncedSearch) params.set("search", debouncedSearch);
    return params.toString();
  }, [debouncedSearch, filters, page, tab]);

  useEffect(() => {
    if (!visibleTabs.some((item) => item.value === tab)) {
      setLoading(false);
      return undefined;
    }

    const controller = new AbortController();
    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const token = localStorage.getItem("authToken");
        const response = await fetch(`${API_URL}/invoices/${invoiceType}?${queryString}`, {
          headers: { Authorization: `Bearer ${token}` }, signal: controller.signal,
        });
        const result = await response.json();
        if (!response.ok || !result.success) throw new Error(result.message || "Unable to load invoices.");
        setRows(result.data?.rows || []);
        setCounts(result.data?.counts || {});
        setGrandTotal(Number(result.data?.grand_total_usd ?? result.data?.grand_total ?? 0));
        setOptions(result.data?.options || { parties: [], invoice_months: [], countries: [] });
        setServerControls(result.data?.controls || {});
        setPagination(result.data?.pagination || emptyPagination);
      } catch (requestError) {
        if (requestError.name !== "AbortError") {
          setRows([]);
          setError(requestError.message || "Unable to load invoices.");
        }
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };
    load();
    return () => controller.abort();
  }, [invoiceType, queryString, reloadKey, tab, visibleTabs]);

  useEffect(() => {
    setSelectedInvoiceIds([]);
  }, [invoiceType, queryString]);

  const selectTab = (nextTab) => {
    setTab(nextTab);
    setPage(1);
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set("tab", nextTab);
    setSearchParams(nextParams, { replace: true });
  };

  const updateFilter = (key, value) => {
    setFilters((current) => ({ ...current, [key]: value }));
    setPage(1);
  };

  const selectParty = (party) => {
    const selected = party || null;
    const countryValue = selected?.invoice_country
      ? (options.countries || []).find((item) => item.value === selected.invoice_country) || { value: selected.invoice_country, label: selected.invoice_country }
      : null;
    setFilters((current) => ({ ...current, party: selected, country: countryValue }));
    setPage(1);
  };

  const rowSelectionKey = (row) => invoiceType === "vendor"
    ? `${row.id}:${row.vendor_id}`
    : String(row.id);
  const allRowsSelected = rows.length > 0 && rows.every((row) => selectedInvoiceIds.includes(rowSelectionKey(row)));
  const someRowsSelected = rows.some((row) => selectedInvoiceIds.includes(rowSelectionKey(row))) && !allRowsSelected;
  const bulkOptions = workflowOptionsForField(bulkAction);
  const selectAllRows = (event) => {
    setSelectedInvoiceIds(event.target.checked ? rows.map(rowSelectionKey) : []);
  };
  const selectRow = (row) => {
    const selectionKey = rowSelectionKey(row);
    setSelectedInvoiceIds((current) => current.includes(selectionKey)
      ? current.filter((selectedId) => selectedId !== selectionKey)
      : [...current, selectionKey]);
  };
  const refreshInvoices = () => setReloadKey((current) => current + 1);
  const showMessage = (message, color = "success") => setSnackbar({ open: true, message, color });

  const exportInvoices = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams({ tab });
      if (filters.startDate) params.set("start_date", filters.startDate);
      if (filters.endDate) params.set("end_date", filters.endDate);
      if (filters.status?.value) params.set("status", filters.status.value);
      if (filters.paymentStatus?.value) params.set("payment_status", filters.paymentStatus.value);
      if (filters.invoiceMonth?.value) params.set("invoice_month", filters.invoiceMonth.value);
      if (filters.emailStatus?.value) params.set("email_status", filters.emailStatus.value);
      if (filters.party?.value) params.set("party_id", String(filters.party.value));
      if (filters.country?.value) params.set("country", filters.country.value);
      if (filters.search.trim()) params.set("search", filters.search.trim());
      selectedInvoiceIds.forEach((invoiceId) => params.append("invoice_ids[]", String(invoiceId)));

      const response = await fetch(`${API_URL}/invoices/${invoiceType}/export?${params.toString()}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}`, Accept: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" },
      });
      if (!response.ok) {
        const result = await response.json().catch(() => ({}));
        throw new Error(result.message || "Unable to export invoices.");
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${invoiceType}-invoices.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      showMessage("Invoice Excel exported successfully.");
    } catch (requestError) {
      showMessage(requestError.message || "Unable to export invoices.", "error");
    } finally {
      setExporting(false);
    }
  };

  const exportPdf = async () => {
    setPdfExporting(true);
    try {
      const params = new URLSearchParams({ tab });
      if (filters.startDate) params.set("start_date", filters.startDate);
      if (filters.endDate) params.set("end_date", filters.endDate);
      if (filters.status?.value) params.set("status", filters.status.value);
      if (filters.paymentStatus?.value) params.set("payment_status", filters.paymentStatus.value);
      if (filters.invoiceMonth?.value) params.set("invoice_month", filters.invoiceMonth.value);
      if (filters.emailStatus?.value) params.set("email_status", filters.emailStatus.value);
      if (filters.party?.value) params.set("party_id", String(filters.party.value));
      if (filters.country?.value) params.set("country", filters.country.value);
      if (filters.search.trim()) params.set("search", filters.search.trim());
      selectedInvoiceIds.forEach((invoiceId) => params.append("invoice_ids[]", String(invoiceId)));

      const response = await fetch(`${API_URL}/invoices/${invoiceType}/export/pdf?${params.toString()}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}`, Accept: "application/zip" },
      });
      if (!response.ok) {
        const result = await response.json().catch(() => ({}));
        throw new Error(result.message || "Unable to export invoices as PDF.");
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${invoiceType}-invoices.zip`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      showMessage("Invoice PDFs ZIP exported successfully.");
    } catch (requestError) {
      showMessage(requestError.message || "Unable to export invoices as PDF.", "error");
    } finally {
      setPdfExporting(false);
    }
  };

  const applyBulkWorkflowUpdate = async () => {
    if (!selectedInvoiceIds.length) {
      showMessage("Please select at least one invoice.", "error");
      return;
    }
    if (!bulkAction || !bulkValue) {
      showMessage("Please select a status type and value.", "error");
      return;
    }

    setWorkflowSaving(true);
    try {
      const workflowPayload = invoiceType === "vendor"
        ? {
            invoice_rows: selectedInvoiceIds.map((selectionKey) => {
              const [invoiceId, vendorId] = String(selectionKey).split(":");
              return { invoice_id: Number(invoiceId), vendor_id: Number(vendorId) };
            }),
            field: bulkAction,
            value: bulkValue,
          }
        : {
            invoice_ids: selectedInvoiceIds.map((invoiceId) => Number(invoiceId)),
            field: bulkAction,
            value: bulkValue,
          };
      const response = await fetch(`${API_URL}/invoices/${invoiceType}/bulk-workflow-status`, {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(workflowPayload),
      });
      const result = await response.json();
      if (!response.ok || !result.success) {
        throw new Error(responseErrorMessage(result, "Unable to update selected invoices."));
      }
      showMessage(result.message || "Selected invoices updated successfully.");
      setSelectedInvoiceIds([]);
      setBulkAction("");
      setBulkValue("");
      refreshInvoices();
    } catch (requestError) {
      showMessage(requestError.message || "Unable to update selected invoices.", "error");
    } finally {
      setWorkflowSaving(false);
    }
  };

  const openActionsMenu = (event, row) => {
    setMenuAnchor(event.currentTarget);
    setMenuInvoice(row);
  };
  const closeActionsMenu = () => {
    setMenuAnchor(null);
    setMenuInvoice(null);
  };
  const openWorkflowDialog = (field) => {
    const row = menuInvoice;
    const options = workflowOptionsForField(field);
    if (!options.length) return;
    const currentValue = row?.workflow_statuses?.[field];
    const defaultValue = options.some((option) => option.value === currentValue)
      ? currentValue
      : options[0].value;
    setWorkflowDialog({
      open: true,
      field,
      value: defaultValue,
      invoice: row,
    });
    closeActionsMenu();
  };
  const closeWorkflowDialog = () => {
    if (workflowSaving) return;
    setWorkflowDialog((current) => ({ ...current, open: false, invoice: null }));
  };
  const updateSingleWorkflowStatus = async () => {
    const { invoice: selectedInvoice, field, value } = workflowDialog;
    if (!selectedInvoice?.id || !field || !value) return;

    setWorkflowSaving(true);
    try {
      const vendorQuery = invoiceType === "vendor" && selectedInvoice.vendor_id
        ? `?vendor_id=${selectedInvoice.vendor_id}`
        : "";
      const response = await fetch(`${API_URL}/invoices/${invoiceType}/${selectedInvoice.id}/workflow-status${vendorQuery}`, {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          [field]: value,
          ...(field === "email_status" && value === "sent" ? { send_email: true } : {}),
        }),
      });
      const result = await response.json();
      if (!response.ok || !result.success) {
        throw new Error(responseErrorMessage(result, "Unable to update invoice status."));
      }
      showMessage(result.message || "Invoice status updated successfully.");
      setWorkflowDialog((current) => ({ ...current, open: false, invoice: null }));
      refreshInvoices();
    } catch (requestError) {
      showMessage(requestError.message || "Unable to update invoice status.", "error");
    } finally {
      setWorkflowSaving(false);
    }
  };

  const canViewAmount = ["usd_amount", "final_amount"].some((field) => canListColumn(field));
  const columns = [
    canListColumn("serial") && "serial",
    canListColumn("invoice_number") && "invoice_number",
    canListColumn("project_code") && "project_code",
    canListColumn("billing_details") && "billing_details",
    canListColumn("invoice_date") && "invoice_date",
    invoiceType !== "vendor" && canListColumn("due_date") && "due_date",
    invoiceType !== "vendor" && canListColumn("reference") && "reference",
    invoiceType !== "vendor" && canListColumn("usd_amount") && "usd_amount",
    canListColumn("final_amount") && "final_amount",
    canListColumn("payment_status") && "payment_status",
    canListColumn("email_status") && "email_status",
    canListColumn("status") && "status",
    canListColumn("actions") && "actions",
  ].filter(Boolean);

  const statusOptions = [
    { value: "approved", label: "Approved" },
    { value: "cancelled", label: "Cancelled" },
    { value: "rejected", label: "Rejected" },
  ];
  const paymentOptions = [{ value: "pending", label: "Pending" }, { value: "paid", label: "Paid" }];
  const emailOptions = [{ value: "pending", label: "Pending" }, { value: "sent", label: "Sent" }];
  const controlSx = { width: "100%", minWidth: 0 };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox my={2}>
        <Card>
          <SoftBox p={{ xs: 1, md: 1 }}>
            <SoftBox display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1} mb={1.25}>
              <SoftTypography variant="h5" fontWeight="medium">{title}</SoftTypography>
            </SoftBox>

            {visibleTabs.length > 0 ? (
              <SoftBox display="grid" gridTemplateColumns={{ xs: "1fr", md: `repeat(${visibleTabs.length}, minmax(0, 1fr))` }} gap={0.8} mb={1.4}>
                {visibleTabs.map((item) => {
                  const style = tabColors[item.value];
                  const active = tab === item.value;
                  return (
                    <SoftBox
                      key={item.value}
                      component="button"
                      type="button"
                      onClick={() => selectTab(item.value)}
                      sx={{
                        border: `1px solid ${style.border}`, borderRadius: "10px", py: 0.8, px: 1,
                        color: style.color, backgroundColor: active ? style.background : "#fff",
                        fontSize: "13px", fontWeight: active ? 700 : 500, cursor: "pointer",
                      }}
                    >
                      {item.label} ({Number(counts[item.value] || 0)})
                    </SoftBox>
                  );
                })}
              </SoftBox>
            ) : (
              <SoftTypography color="error" variant="button">No invoice tab permission has been assigned.</SoftTypography>
            )}

            <SoftBox mb={1.2}>
              <SoftBox
                display="grid"
                gridTemplateColumns={{ xs: "1fr", sm: "repeat(2, minmax(0, 1fr))", lg: "repeat(6, minmax(0, 1fr))" }}
                alignItems="end"
                gap={0.8}
                mb={0.8}
              >
                {canFilter("start_date") && <ClearableDate label="Start Date (Created)" value={filters.startDate} onChange={(value) => updateFilter("startDate", value)} />}
                {canFilter("end_date") && <ClearableDate label="End Date (Created)" value={filters.endDate} onChange={(value) => updateFilter("endDate", value)} />}
                {canFilter("status") && <SoftBox sx={controlSx}><SoftTypography variant="caption">Status</SoftTypography><SoftSelect size="small" placeholder="Status" value={filters.status} options={statusOptions} isClearable onChange={(value) => updateFilter("status", value || null)} /></SoftBox>}
                {canFilter("payment_status") && <SoftBox sx={controlSx}><SoftTypography variant="caption">Payment Status</SoftTypography><SoftSelect size="small" placeholder="Payment Status" value={filters.paymentStatus} options={paymentOptions} isClearable onChange={(value) => updateFilter("paymentStatus", value || null)} /></SoftBox>}
                {canFilter("invoice_month") && <SoftBox sx={controlSx}><SoftTypography variant="caption">Invoice Year Month</SoftTypography><SoftSelect size="small" placeholder="Year - Month" value={filters.invoiceMonth} options={(options.invoice_months || []).map((month) => ({ value: month, label: month }))} isClearable onChange={(value) => updateFilter("invoiceMonth", value || null)} /></SoftBox>}
                {canFilter("email_status") && <SoftBox sx={controlSx}><SoftTypography variant="caption">Email Status</SoftTypography><SoftSelect size="small" placeholder="Email Status" value={filters.emailStatus} options={emailOptions} isClearable onChange={(value) => updateFilter("emailStatus", value || null)} /></SoftBox>}
              </SoftBox>
              <SoftBox
                display={{ xs: "grid", sm: "grid", lg: "flex" }}
                gridTemplateColumns={{ xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" }}
                alignItems="end"
                gap={0.8}
              >
                {canFilter(invoiceType) && <SoftBox sx={{ flex: "1 1 220px", maxWidth: { lg: 250 }, minWidth: 0 }}><SoftTypography variant="caption">{partyLabel}</SoftTypography><SoftSelect size="small" placeholder={partyLabel} value={filters.party} options={options.parties || []} isSearchable isClearable onChange={selectParty} /></SoftBox>}
              {canFilter("country") && (
                <SoftBox sx={{ width: 155, minWidth: 155, flexShrink: 0 }}>
                  <SoftTypography variant="caption">Invoice Country</SoftTypography>
                  <SoftSelect size="small" placeholder="Invoice Country" value={filters.country} options={options.countries || []} isClearable onChange={(value) => updateFilter("country", value || null)} />
                </SoftBox>
              )}
              {canFilter("search") && (
                <SoftBox sx={{ flex: "1 1 220px", maxWidth: { lg: 250 }, minWidth: 0 }}>
                  <SoftTypography variant="caption">Search</SoftTypography>
                  <SoftBox display="flex" alignItems="center" gap={0.25}>
                    <SoftInput size="small" placeholder="Invoice, project, billing..." value={filters.search} onChange={(event) => updateFilter("search", event.target.value)} />
                    {filters.search && <IconButton size="small" onClick={() => updateFilter("search", "")}><Icon sx={{ fontSize: "16px !important" }}>close</Icon></IconButton>}
                  </SoftBox>
                </SoftBox>
              )}
              {canExport && (
                <SoftButton
                  color="info"
                  size="small"
                  disabled={exporting}
                  onClick={exportInvoices}
                  sx={{ width: "fit-content", minHeight: "40px", px: 1, fontSize: "12px", whiteSpace: "nowrap" }}
                >
                  <Icon>download</Icon>&nbsp; {exporting
                    ? "Exporting..."
                    : selectedInvoiceIds.length > 0
                      ? `Export Selected (${selectedInvoiceIds.length})`
                      : "Export Excel"}
                </SoftButton>
              )}
              {canExportPdf && (
                <SoftButton
                  color="error"
                  size="small"
                  disabled={pdfExporting}
                  onClick={exportPdf}
                  sx={{ width: "fit-content", minHeight: "40px", px: 1, fontSize: "12px", whiteSpace: "nowrap" }}
                >
                  <Icon>picture_as_pdf</Icon>&nbsp; {pdfExporting
                    ? "Generating..."
                    : selectedInvoiceIds.length > 0
                      ? `Export PDF (${selectedInvoiceIds.length})`
                      : "Export PDF"}
                </SoftButton>
              )}
              {canCreateClientInvoice && (
                <SoftButton
                  color="info"
                  size="small"
                  onClick={() => navigate("/invoices/client/create")}
                  sx={{ width: "fit-content", minHeight: "40px", px: 1, fontSize: "12px", whiteSpace: "nowrap" }}
                >
                  <Icon>add</Icon>&nbsp; Generate Invoice
                </SoftButton>
              )}
              </SoftBox>
            </SoftBox>

            <SoftBox display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={0.5} mb={1}>
              {canListControl("showing_entries") && <SoftTypography variant="caption" color="text">Showing {pagination.from} to {pagination.to} of {pagination.total} entries</SoftTypography>}
              {canListControl("grand_total") && canViewAmount && (
                <SoftTypography variant="button" color="info">
                  {invoiceType === "vendor" ? "USD Amount" : "Exchange Amount"}: ${grandTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USD
                </SoftTypography>
              )}
            </SoftBox>

            {canSelectRows && (
              <SoftBox
                display="flex"
                alignItems="center"
                flexWrap="wrap"
                gap={0.75}
                p={1}
                mb={1}
                sx={{ border: "1px solid #e2e8f0", borderRadius: "8px", backgroundColor: "#f8fafc" }}
              >
                <SoftTypography variant="caption" fontWeight="medium" sx={{ minWidth: 105 }}>
                      {selectedInvoiceIds.length} selected
                </SoftTypography>
                <SoftBox sx={{ width: { xs: "100%", sm: 220 } }}>
                  <SoftSelect
                    size="small"
                    placeholder="Select Action"
                    value={selectedOption(availableBulkWorkflowActionOptions, bulkAction)}
                    options={availableBulkWorkflowActionOptions}
                    onChange={(option) => {
                      setBulkAction(option?.value || "");
                      setBulkValue("");
                    }}
                    isClearable
                  />
                </SoftBox>
                <SoftBox sx={{ width: { xs: "100%", sm: 200 } }}>
                  <SoftSelect
                    size="small"
                    placeholder={bulkAction ? `Select ${workflowFieldLabels[bulkAction]}` : "Select Value"}
                    value={selectedOption(bulkOptions, bulkValue)}
                    options={bulkOptions}
                    onChange={(option) => setBulkValue(option?.value || "")}
                    isDisabled={!bulkAction}
                    isClearable
                  />
                </SoftBox>
                <SoftButton
                  color="info"
                  size="small"
                  disabled={workflowSaving || !selectedInvoiceIds.length || !bulkAction || !bulkValue}
                  onClick={applyBulkWorkflowUpdate}
                  sx={{ minHeight: 36 }}
                >
                  {workflowSaving ? "Updating..." : "Apply"}
                </SoftButton>
              </SoftBox>
            )}

            {loading ? (
              <SoftBox py={6} display="flex" justifyContent="center"><CircularProgress color="info" /></SoftBox>
            ) : error ? (
              <SoftTypography variant="button" color="error">{error}</SoftTypography>
            ) : (
              <TableContainer component={Paper} sx={{ boxShadow: "none", overflowX: "hidden", width: "100%", border: "1px solid #edf2f7" }}>
                <Table size="small" sx={{ width: "100%", minWidth: 0, tableLayout: "fixed" }}>
                  <TableHead sx={{ display: "table-header-group" }}>
                    <TableRow>
                      {canSelectRows && (
                        <TableCell sx={{ ...headerCellSx, width: 28, minWidth: 28, maxWidth: 28, padding: "0 !important" }}>
                          <Checkbox
                            size="small"
                            sx={{ padding: "0 !important", margin: "0 !important" }}
                            checked={allRowsSelected}
                            indeterminate={someRowsSelected}
                            onChange={selectAllRows}
                          />
                        </TableCell>
                      )}
                      {columns.includes("serial") && <TableCell sx={{ ...headerCellSx, width: 34, minWidth: 34, maxWidth: 34 }}>#</TableCell>}
                      {columns.includes("invoice_number") && <TableCell sx={{ ...headerCellSx, width: "8%", minWidth: 0 }}>{invoiceType === "vendor" ? "PO Number" : "Invoice Number"}</TableCell>}
                      {columns.includes("project_code") && <TableCell sx={headerCellSx}>Project Code</TableCell>}
                      {columns.includes("billing_details") && <TableCell sx={{ ...headerCellSx, width: "10%", minWidth: 0 }}>{invoiceType === "vendor" ? "Vendor Details" : "Billing Details"}</TableCell>}
                      {columns.includes("invoice_date") && <TableCell sx={headerCellSx}>{invoiceType === "vendor" ? "PO Date" : "Invoice Date"}</TableCell>}
                      {columns.includes("due_date") && <TableCell sx={headerCellSx}>Due Date</TableCell>}
                      {columns.includes("reference") && <TableCell sx={headerCellSx}>Vendor Reference</TableCell>}
                      {columns.includes("usd_amount") && <TableCell sx={headerCellSx}>USD Amount</TableCell>}
                      {columns.includes("final_amount") && <TableCell sx={headerCellSx}>{invoiceType === "vendor" ? "USD Amount" : "Exchange Amount"}</TableCell>}
                      {columns.includes("payment_status") && <TableCell sx={headerCellSx}>Payment Status</TableCell>}
                      {columns.includes("email_status") && <TableCell sx={headerCellSx}>Email Status</TableCell>}
                      {columns.includes("status") && <TableCell sx={headerCellSx}>Status</TableCell>}
                      {columns.includes("actions") && (
                        <TableCell sx={{ ...headerCellSx, width: 28, minWidth: 28, maxWidth: 28, p: 0 }} align="center" aria-label="Actions">
                          <Icon sx={{ fontSize: "18px !important" }}>more_vert</Icon>
                        </TableCell>
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map((row, rowIndex) => {
                      const projectCodes = projectCodesForRow(row);
                      const eInvoiceStatus = String(row.e_invoice_status || "").toLowerCase();
                      const eInvoiceRowSx = invoiceType === "client" && eInvoiceStatus === "generated"
                        ? { backgroundColor: "#dcfce7", "&:hover": { backgroundColor: "#bbf7d0" } }
                        : invoiceType === "client" && eInvoiceStatus === "cancelled"
                          ? { backgroundColor: "#fee2e2", "&:hover": { backgroundColor: "#fecaca" } }
                          : undefined;
                      return (
                        <TableRow key={`${row.id}-${row.vendor_id || "client"}`} hover sx={eInvoiceRowSx}>
                          {canSelectRows && (
                            <TableCell sx={{ ...bodyCellSx, width: 28, minWidth: 28, maxWidth: 28, padding: "0 !important" }}>
                              <Checkbox
                                size="small"
                                sx={{ padding: "0 !important", margin: "0 !important" }}
                                checked={selectedInvoiceIds.includes(rowSelectionKey(row))}
                                onChange={() => selectRow(row)}
                              />
                            </TableCell>
                          )}
                          {columns.includes("serial") && <TableCell sx={{ ...bodyCellSx, width: 34, minWidth: 34, maxWidth: 34 }}>{(pagination.from || 1) + rowIndex}</TableCell>}
                          {columns.includes("invoice_number") && <TableCell sx={{ ...bodyCellSx, width: "8%", minWidth: 0 }}>{row.invoice_number || "-"}</TableCell>}
                          {columns.includes("project_code") && (
                            <TableCell sx={bodyCellSx}>
                              <Tooltip title={projectCodes.join(", ")} disableHoverListener={projectCodes.length < 2} arrow>
                                <SoftBox
                                  component="span"
                                  sx={{ cursor: projectCodes.length > 1 ? "help" : "default", borderBottom: projectCodes.length > 1 ? "1px dotted #64748b" : "none" }}
                                >
                                  {projectCodes[0] || "-"}
                                </SoftBox>
                              </Tooltip>
                            </TableCell>
                          )}
                          {columns.includes("billing_details") && (() => {
                            const billingName = String(row.billing_details?.name || "-");
                            const shortBillingName = billingName.length > 8 ? `${billingName.slice(0, 8)}…` : billingName;
                            return (
                              <TableCell sx={{ ...bodyCellSx, width: "10%", minWidth: 0, maxWidth: "10%", whiteSpace: "nowrap" }}>
                                <Tooltip title={billingName} disableHoverListener={billingName.length <= 8} arrow>
                                  <SoftBox component="span" sx={{ cursor: billingName.length > 8 ? "help" : "default" }}>
                                    <strong>{shortBillingName}</strong>
                                  </SoftBox>
                                </Tooltip>
                              </TableCell>
                            );
                          })()}
                          {columns.includes("invoice_date") && <TableCell sx={bodyCellSx}>{row.invoice_date || "-"}{invoiceType !== "vendor" && <DueCountdown row={row} />}</TableCell>}
                          {columns.includes("due_date") && <TableCell sx={bodyCellSx}>{row.due_date || "-"}</TableCell>}
                          {columns.includes("reference") && <TableCell sx={bodyCellSx}>{row.reference_number || "-"}</TableCell>}
                          {columns.includes("usd_amount") && <TableCell sx={bodyCellSx}>{formatCurrency(row.source_amount_usd, "$", "USD", true)}</TableCell>}
                          {columns.includes("final_amount") && (
                            <TableCell sx={bodyCellSx}>
                              {formatCurrency(row.final_amount ?? row.amount, currencySymbolFor(row), row.currency_code, true)}
                            </TableCell>
                          )}
                          {columns.includes("payment_status") && (
                            <TableCell sx={bodyCellSx}>
                              <SoftTypography component="span" variant="button" fontWeight="medium" color={row.payment_status === "paid" ? "success" : "warning"}>
                                {row.payment_status === "paid" ? "Paid" : "Pending"}
                              </SoftTypography>
                            </TableCell>
                          )}
                          {columns.includes("email_status") && <TableCell sx={bodyCellSx}>{row.email_status === "sent" ? "Sent" : "Pending"}</TableCell>}
                          {columns.includes("status") && <TableCell sx={bodyCellSx}>{String(row.status || "-").replace("_", " ")}</TableCell>}
                          {columns.includes("actions") && (
                            <TableCell sx={{ ...bodyCellSx, width: 28, minWidth: 28, maxWidth: 28, p: 0, whiteSpace: "nowrap" }} align="center">
                              <Tooltip title="Actions">
                                <IconButton size="small" sx={{ p: 0 }} onClick={(event) => openActionsMenu(event, row)}>
                                  <Icon>more_vert</Icon>
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          )}
                        </TableRow>
                      );
                    })}
                    {rows.length === 0 && <TableRow><TableCell colSpan={Math.max(columns.length + (canSelectRows ? 1 : 0), 1)} align="center" sx={{ ...bodyCellSx, py: 4 }}>No invoices found.</TableCell></TableRow>}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {canListControl("pagination") && pagination.last_page > 1 && (
              <SoftBox display="flex" justifyContent="flex-end" alignItems="center" mt={1}>
                <SoftPagination item disabled={page === 1} onClick={() => setPage(1)}><Icon>first_page</Icon></SoftPagination>
                <SoftPagination item disabled={page === 1} onClick={() => setPage((value) => Math.max(1, value - 1))}><Icon>chevron_left</Icon></SoftPagination>
                <SoftTypography variant="caption" mx={1}>{pagination.current_page} / {pagination.last_page}</SoftTypography>
                <SoftPagination item disabled={page >= pagination.last_page} onClick={() => setPage((value) => Math.min(pagination.last_page, value + 1))}><Icon>chevron_right</Icon></SoftPagination>
                <SoftPagination item disabled={page >= pagination.last_page} onClick={() => setPage(pagination.last_page)}><Icon>last_page</Icon></SoftPagination>
              </SoftBox>
            )}
          </SoftBox>
        </Card>
      </SoftBox>
      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={closeActionsMenu}>
        {menuInvoice?.actions?.view && hasModulePermission(invoiceActionPermission(invoiceType, "view")) && (
          <MenuItem onClick={() => {
            const vendorQuery = invoiceType === "vendor" && menuInvoice?.vendor_id
              ? `?vendor_id=${menuInvoice.vendor_id}`
              : "";
            navigate(`/invoices/${invoiceType}/${menuInvoice.id}/view${vendorQuery}`);
            closeActionsMenu();
          }}>
            <Icon sx={{ mr: 1 }}>visibility</Icon> View
          </MenuItem>
        )}
        {menuInvoice?.actions?.edit && hasModulePermission(invoiceActionPermission(invoiceType, "edit")) && (
          <MenuItem onClick={() => { navigate(`/invoices/${invoiceType}/${menuInvoice.id}/edit`); closeActionsMenu(); }}>
            <Icon sx={{ mr: 1 }}>edit</Icon> Edit
          </MenuItem>
        )}
        {menuInvoice?.actions?.update_workflow && canUpdateWorkflow && <Divider />}
        {menuInvoice?.actions?.update_workflow && canUpdateWorkflow && availableWorkflowActionOptions
          .filter((option) => menuInvoice?.workflow_actions?.[option.value] !== false)
          .map((option) => (
          <MenuItem key={option.value} onClick={() => openWorkflowDialog(option.value)}>
            <Icon sx={{ mr: 1 }}>sync_alt</Icon> {option.label}
          </MenuItem>
        ))}
      </Menu>

      <Dialog open={workflowDialog.open} onClose={closeWorkflowDialog} fullWidth maxWidth="xs">
        <DialogTitle>
          Update {workflowFieldLabels[workflowDialog.field]}
        </DialogTitle>
        <DialogContent sx={{ pt: "12px !important", overflow: "visible" }}>
          <SoftTypography variant="caption" display="block" mb={0.5}>
            Invoice: {workflowDialog.invoice?.invoice_number || "-"}
          </SoftTypography>
          <SoftSelect
            value={selectedOption(workflowOptionsForField(workflowDialog.field), workflowDialog.value)}
            options={workflowOptionsForField(workflowDialog.field)}
            onChange={(option) => setWorkflowDialog((current) => ({ ...current, value: option?.value || "" }))}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <SoftButton variant="outlined" color="secondary" size="small" disabled={workflowSaving} onClick={closeWorkflowDialog}>
            Cancel
          </SoftButton>
          <SoftButton color="info" size="small" disabled={workflowSaving || !workflowDialog.value} onClick={updateSingleWorkflowStatus}>
            {workflowSaving ? "Updating..." : "Update"}
          </SoftButton>
        </DialogActions>
      </Dialog>
      <Footer />
      <SoftSnackbar
        color={snackbar.color}
        icon={snackbar.color === "success" ? "check" : "warning"}
        title={snackbar.color === "success" ? "Success" : "Update Failed"}
        content={snackbar.message}
        open={snackbar.open}
        onClose={() => setSnackbar((current) => ({ ...current, open: false }))}
        close={() => setSnackbar((current) => ({ ...current, open: false }))}
      />
    </DashboardLayout>
  );
}

InvoiceList.propTypes = {
  invoiceType: PropTypes.oneOf(["client", "vendor"]).isRequired,
};

export default InvoiceList;
